import type { SubscriptionPlan } from '@/types/subscription';
import { formatCurrency } from '@/utils/currency';
import data from './plans.data.json';

/**
 * The three plans with their US-dollar prices, for the pages that are prerendered at build time (no API at build) and as the
 * placeholder the pricing table shows while the live catalog loads. The live catalog (Stripe) stays the truth for buying: these
 * entries carry no real price ids and are never sent to checkout.
 *
 * The numbers are written once, in plans.data.json (monthly price in cents; annual is `annualMonths` months, two months free).
 * The dev mock catalog, the test fixtures, the prerender checks, the llms.txt test and the nginx smoke test all read that file.
 */
export const PLANS: { code: string; name: string; seats: number; monthly: number }[] = data.plans.map((p) => ({
  code: p.code,
  name: p.name,
  seats: p.seats,
  monthly: p.monthlyCents,
}));

export const ANNUAL_MONTHS: number = data.annualMonths;

/** The prices as the pricing table shows them, e.g. planPriceStrings.solo = { month: '$49.99', year: '$499.90' }. */
export const planPriceStrings: Record<string, { month: string; year: string }> = Object.fromEntries(
  PLANS.map((p) => [p.code, { month: formatCurrency(p.monthly / 100), year: formatCurrency((p.monthly * ANNUAL_MONTHS) / 100) }]),
);

export const STATIC_PLANS: SubscriptionPlan[] = PLANS.map((p) => ({
  id: `static_${p.code}`,
  name: p.name,
  description: '',
  metadata: { plan_code: p.code, seats: String(p.seats) },
  marketingFeatures: [],
  limits: { maxUsers: p.seats },
  prices: (['month', 'year'] as const).map((interval) => ({
    id: `static_${p.code}_${interval}`,
    currency: 'usd',
    unitAmount: interval === 'month' ? p.monthly : p.monthly * ANNUAL_MONTHS,
    recurring: { interval, intervalCount: 1 },
    type: 'recurring' as const,
  })),
}));

/** True while the build-time prerender script is rendering the page (it sets this before the app starts). */
export const isPrerendering = (): boolean => typeof window !== 'undefined' && (window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__ === true;
